// TermsAndConditions.jsx
// A polished Terms & Conditions page using React + Tailwind.
// Includes: sticky Table of Contents (desktop), clean readable typography,
// subtle card, print/download button, and accessible anchors.
// NOTE: See the bottom of this file for an example Layout that hides
// Header/Footer specifically on this route.

import React from "react";

// Utility: section meta so ToC and content stay in sync
const SECTIONS = [
  { id: "acceptance", title: "1. Acceptance of Terms" },
  { id: "who-we-are", title: "2. Who We Are" },
  { id: "use", title: "3. Use of Our Website" },
  { id: "registration", title: "4. Registration & Data" },
  { id: "scholarship", title: "5. Scholarship & Admission Policies" },
  { id: "fees", title: "6. Fee Payment Terms" },
  { id: "intellectual", title: "7. Intellectual Property" },
  { id: "liability", title: "8. Limitation of Liability" },
  { id: "links", title: "9. External Links" },
  { id: "changes", title: "10. Changes to Terms" },
  { id: "contact", title: "11. Contact Us" },
];

export default function TermsAndConditions() {
  return (
    <main className="min-h-screen bg-neutral-50">
      {/* Page header */}
      <div className="bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 text-white">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:py-16">
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Terms & Conditions
          </h1>
          <p className="mt-2 text-white/90">
            Rankridge Junior College – IIT, NEET, EAMCET Coaching
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-sm">
            <span className="inline-block h-2 w-2 rounded-full bg-lime-300" />
            Effective Date: 05‑05‑2025
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 py-10 lg:grid-cols-[280px_1fr]">
        {/* TOC (desktop) */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-neutral-500">
              On this page
            </p>
            <nav className="space-y-1">
              {SECTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="block rounded-lg px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-teal-700"
                >
                  {s.title}
                </a>
              ))}
            </nav>
            <hr className="my-4" />
            <button
              onClick={() => window.print()}
              className="w-full rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-teal-700"
            >
              Print / Save PDF
            </button>
          </div>
        </aside>

        {/* Main card */}
        <article className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
          <p className="mb-6 text-sm text-neutral-600">
            Welcome to <span className="font-medium text-neutral-800">rankridge.com</span>, the official online platform of <span className="font-medium text-neutral-800">Rankridge Junior College</span> (IIT‑JEE, NEET, EAMCET long‑term coaching). By using our website, you agree to these Terms. Please read them carefully.
          </p>

          {/* Sections */}
          <Section id="acceptance" title="1. Acceptance of Terms">
            By accessing our website, you confirm that you accept these Terms and agree to follow them. If you do not agree with any part, please do not use the website.
          </Section>

          <Section id="who-we-are" title="2. Who We Are">
            Rankridge is a leading junior college offering IIT‑JEE, NEET, and EAMCET long‑term coaching. Our aim is to help students reach their academic goals through structured programs, expert faculty, and regular performance tracking.
          </Section>

          <Section id="use" title="3. Use of Our Website">
            <ul className="list-disc ps-5">
              <li>You must be at least 13 years of age to use this site.</li>
              <li>Use the site only for lawful purposes. Do not attempt unauthorized access.</li>
              <li>All content (including text, images, videos, and logos) is the property of Rankridge and may not be copied, reused, or distributed without written permission.</li>
            </ul>
          </Section>

          <Section id="registration" title="4. Registration & Data">
            To apply for courses, scholarships, counseling, or inquiries, you agree to provide accurate and complete information. Your data will be kept secure and used only as described in our Privacy Policy and for services related to Rankridge.
          </Section>

          <Section id="scholarship" title="5. Scholarship & Admission Policies">
            Rankridge offers scholarships through transparent criteria (including our Entrance Test). Eligibility, selection, and benefits are governed by official Rankridge policies. We reserve the right to amend, modify, or cancel offers at any time with prior notice.
          </Section>

          <Section id="fees" title="6. Fee Payment Terms">
            <ul className="list-disc ps-5">
              <li>Fees are payable as communicated during admission.</li>
              <li>We accept UPI, net banking, or card payments at our campus offices or secure online portals.</li>
              <li>Please ensure you collect an official receipt for any payment. Rankridge is not responsible for payments made to unofficial sources.</li>
            </ul>
          </Section>

          <Section id="intellectual" title="7. Intellectual Property">
            All materials on this website are owned by or licensed to Rankridge and protected under applicable laws. They are intended for personal use only and must not be shared, edited, or redistributed without permission.
          </Section>

          <Section id="liability" title="8. Limitation of Liability">
            While we strive to provide accurate and up‑to‑date information, Rankridge does not guarantee that the website will always be available, error‑free, or secure. We are not responsible for any losses or damages resulting from your use of the website.
          </Section>

          <Section id="links" title="9. External Links">
            Our website may contain links to third‑party sites. These are provided for convenience only. Rankridge does not control and is not responsible for the content or practices of those sites.
          </Section>

          <Section id="changes" title="10. Changes to Terms">
            We may update these Terms from time to time. When we do, we will update the “Effective Date” shown at the top of this page. Your continued use of the site means you accept the changes.
          </Section>

          <Section id="contact" title="11. Contact Us">
            If you have any questions about these Terms, please contact us at
            <span className="ms-1 font-medium text-neutral-800">support@rankridge.com</span> or visit our campus office.
          </Section>

          <hr className="my-8" />
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <p className="text-sm text-neutral-500">© {new Date().getFullYear()} Rankridge Junior College. All rights reserved.</p>
            <div className="flex gap-2">
              <a href="#top" className="rounded-lg border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50">Back to top</a>
              <button onClick={() => window.print()} className="rounded-lg bg-neutral-900 px-3 py-2 text-sm font-semibold text-white hover:bg-neutral-800">Print / Save PDF</button>
            </div>
          </div>
        </article>
      </div>
    </main>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-semibold text-neutral-900">
        <span className="me-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-teal-600 text-xs font-bold text-white">{title.split(".")[0]}</span>
        {title.slice(title.indexOf(" ") + 1)}
      </h2>
      <div className="prose mt-3 max-w-none text-neutral-700 prose-p:leading-relaxed prose-li:leading-relaxed">
        {typeof children === "string" ? <p>{children}</p> : children}
      </div>
      <div className="my-6 h-px w-full bg-gradient-to-r from-transparent via-neutral-200 to-transparent" />
    </section>
  );
}

/* ============================================================
   OPTIONAL: Layout example that hides Header/Footer on /terms
   ------------------------------------------------------------
   If you have a global layout with Header/Footer, you can
   conditionally hide them on specific routes like "/terms".
   Add this to your layout file and wrap your routes with it.

import { Outlet, useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";

export function AppLayout() {
  const { pathname } = useLocation();
  const hideChromeOn = ["/terms"]; // add more paths as needed
  const hideChrome = hideChromeOn.includes(pathname);

  return (
    <div className="min-h-screen bg-neutral-50">
      {!hideChrome && <Header />}
      <Outlet />
      {!hideChrome && <Footer />}
    </div>
  );
}

// In your router:
// <Route element={<AppLayout/>}>
//   <Route path="/" element={<Home/>} />
//   <Route path="/terms" element={<TermsAndConditions/>} />
// </Route>
*/
